import { fireSEQ } from '@driftt/seq-launcher'
import { Button } from '@driftt/tide-core'
import { Modal } from '@driftt/tide-modal'
import { showTransientNotification } from '@driftt/traffic-control'
import produce from 'immer'
import * as R from 'ramda'
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { saveDraft, savePlaybook } from 'api/playbooks'
import { setPlaybookType } from 'modules/editor/actions'
import { receivePlaybook } from 'modules/playbook/actions'
import { isChatPlaybook, isFastLanePlaybook } from 'utils/playbooks'
import { trackExperimentEnded, trackExperimentStarted } from 'utils/tracking'

import EndExperimentModal from './EndExperimentModal'
import StartExperimentModal from './StartExperimentModal'

const PublishButton = ({
  hasExperiment,
  playbook,
  publishedPlaybook,
  onPostPublish,
  isExperimentEnabled,
  isSaving,
  isDisabled,
  trackPublishAndResetTimer,
}) => {
  const dispatch = useDispatch()
  const [isPublishing, setIsPublishing] = useState(false)
  const [showEndExperimentModal, setShowEndExperimentModal] = useState(false)
  const [showStartExperimentModal, setShowStartExperimentModal] =
    useState(false)

  const user = useSelector(R.prop('user'))

  let buttonText = 'Publish'
  if (hasExperiment) {
    buttonText = isExperimentEnabled ? 'End A/B test' : 'Start A/B test'
  }

  const buttonType = isExperimentEnabled ? 'secondary' : 'primary'

  const handlePublishPlaybook = async (
    basePlaybook = playbook,
    successMessage = 'Playbook successfully published.'
  ) => {
    setIsPublishing(true)

    try {
      const publishCandidate = produce(basePlaybook, (draft) => {
        delete draft.status
        delete draft.name
      })

      saveDraft({
        playbook: { ...basePlaybook, updatedAuthorId: user.id },
      })

      const { data: publishedPlaybook } = await savePlaybook({
        playbook: publishCandidate,
      })

      if (hasExperiment && !isExperimentEnabled) {
        trackExperimentStarted()
      }

      dispatch(receivePlaybook(publishedPlaybook))
      dispatch(setPlaybookType(publishedPlaybook.reportType))

      onPostPublish(publishedPlaybook)

      dispatch(
        showTransientNotification({
          type: 'success',
          message: successMessage,
        })
      )

      const isFastlane = isFastLanePlaybook(publishedPlaybook)

      if (isFastlane) {
        fireSEQ('PUBLISH_FAST_LANE_PLAYBOOK')
      } else if (isChatPlaybook(publishedPlaybook.reportType)) {
        fireSEQ('PUBLISH_BOT_PLAYBOOK')
      }
    } catch (err) {
      if (err.response.status === 409) {
        dispatch(
          showTransientNotification({
            type: 'error',
            message:
              'Oops! This Driftlink is already used by a different playbook.',
          })
        )
      } else {
        dispatch(
          showTransientNotification({
            type: 'error',
            message: `Could not publish your playbook.`,
          })
        )
      }
    } finally {
      setIsPublishing(false)
      trackPublishAndResetTimer()
    }
  }

  const handleEditExperiment = () => {
    const experimentDuration = Date.now() - publishedPlaybook.updatedAt
    trackExperimentEnded({ duration: experimentDuration })

    const newPlaybook = produce(playbook, (draft) => {
      draft.experiment.enabled = false
    })

    handlePublishPlaybook(newPlaybook, 'Your A/B test has ended')
  }

  const handleEndExperiment = ({ useVariant }) => {
    const experimentDuration = Date.now() - publishedPlaybook.updatedAt
    const winner = useVariant ? 'B' : 'A'
    trackExperimentEnded({
      duration: experimentDuration,
      winner: winner,
    })

    const newPlaybook = produce(playbook, (draft) => {
      if (useVariant) {
        draft.actions = draft.experiment.actions
        draft.actionGroups = draft.experiment.actionGroups
        draft.goals = draft.experiment.goals
      }
      delete draft.experiment
    })

    handlePublishPlaybook(newPlaybook, 'A/B test has ended')
  }

  const handleStartExperiment = () => {
    const newPlaybook = produce(playbook, (draft) => {
      draft.experiment.enabled = true
    })

    handlePublishPlaybook(newPlaybook, 'A/B test started successfully')
  }

  const handleClick = () => {
    if (isExperimentEnabled) {
      setShowEndExperimentModal(true)
    } else if (hasExperiment) {
      setShowStartExperimentModal(true)
    } else {
      handlePublishPlaybook()
    }
  }

  const isDisabledWithoutExperimentErrorCheck = () => {
    if (isExperimentEnabled && isDisabled) {
      return false
    }
    if (!isExperimentEnabled && !isDisabled) {
      return false
    }
    if (!isExperimentEnabled && isDisabled) {
      return true
    }
  }

  return (
    <>
      <Button
        className="playbook-status-bar-button"
        type={buttonType}
        onClick={handleClick}
        loading={isPublishing || isSaving}
        disabled={isDisabledWithoutExperimentErrorCheck()}
      >
        {buttonText}
      </Button>
      <Modal
        size="small"
        open={showEndExperimentModal}
        onClose={() => setShowEndExperimentModal(false)}
      >
        <EndExperimentModal
          publishedPlaybook={publishedPlaybook}
          onEndExperiment={handleEndExperiment}
          onEditExperiment={handleEditExperiment}
        />
      </Modal>
      <Modal
        size="small"
        open={showStartExperimentModal}
        onClose={() => setShowStartExperimentModal(false)}
      >
        <StartExperimentModal onStartExperiment={handleStartExperiment} />
      </Modal>
    </>
  )
}
export default PublishButton
